import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PlanGenerateInput } from "../../types"
import type { PlanLibraryDoc } from "../../utils/plan-library-docs"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

import { COMPANY_NAME } from "../../utils/branding"
import { SegmentAiDisclaimer } from "../segment-ai-disclaimer"

export type { PlanLibraryDoc }

/**
 * Generate modal. When a document library is available the user can focus the
 * generate on an area and select source documents; otherwise the plan is built
 * from the segment + company evidence already on file.
 */
export function GenerateModal({
  open,
  onOpenChange,
  kind,
  companyName,
  libraryDocs = [],
  isGenerating,
  onGenerate,
  testIdPrefix = "sa-plan",
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  kind: "ccp" | "competitor"
  companyName: string
  libraryDocs?: PlanLibraryDoc[]
  isGenerating: boolean
  onGenerate: (input: PlanGenerateInput) => void
  testIdPrefix?: string
}) {
  const { t } = useTranslation()
  const [area, setArea] = useState("")
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setArea("")
      setSelected([])
    }
  }, [open])

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function submit() {
    const input: PlanGenerateInput = {}
    if (area.trim()) input.area = area.trim()
    if (selected.length > 0) input.sourceDocIds = selected
    onGenerate(input)
  }

  const description =
    kind === "ccp"
      ? t("segmentAnalysis.generateModal.ccpDescription", { company: companyName })
      : t("segmentAnalysis.generateModal.competitorDescription", {
          company: companyName,
          organization: COMPANY_NAME,
        })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("segmentAnalysis.generateModal.title")}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-4">
          <label className="grid gap-1.5">
            <span className="text-foreground text-sm font-medium">
              {t("segmentAnalysis.generateModal.areaLabel")}
            </span>
            <Input
              data-testid={`${testIdPrefix}-generate-area`}
              placeholder={t("segmentAnalysis.generateModal.areaPlaceholder")}
              value={area}
              onChange={(event) => setArea(event.target.value)}
            />
          </label>
          {libraryDocs.length > 0 ? (
            <div className="grid gap-2">
              <span className="text-foreground text-sm font-medium">
                {t("segmentAnalysis.generateModal.sourcesLabel")}
              </span>
              <div className="border-border max-h-48 overflow-y-auto rounded-md border p-2">
                {libraryDocs.map((doc) => (
                  <label
                    key={doc.id}
                    className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm"
                  >
                    <Checkbox
                      checked={selected.includes(doc.id)}
                      onCheckedChange={() => toggle(doc.id)}
                    />
                    <span className="truncate">{doc.title}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground border-border rounded-md border border-dashed px-3 py-2 text-xs">
              {t("segmentAnalysis.generateModal.noSources")}
            </p>
          )}
        </DialogBody>
        <DialogFooter className="flex-wrap items-center gap-2">
          <SegmentAiDisclaimer className="mr-auto" />
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("segmentAnalysis.generateModal.cancel")}
          </Button>
          <Button
            data-testid={`${testIdPrefix}-generate-submit`}
            disabled={isGenerating}
            size="sm"
            onClick={submit}
          >
            {isGenerating
              ? t("segmentAnalysis.plan.generating")
              : t("segmentAnalysis.generateModal.generate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
